import type {ReactElement} from 'react';
import {useMemo} from 'react';
import React, {useState} from 'react';
import {ItemHeaderTab} from '@Components/poster-editor/components/poster-editing-side-panel/poster-editing-side-panel.types';
import {ContextualMenu} from '@Components/contextual-menu';
import {ItemPositionContextualOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-position-contextual-options';
import {DefaultEditingOptionsHeader} from '@Components/poster-editor/components/poster-editing-side-panel/components/default-editing-options-header';
import {PosterItemControls} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls';
import {ItemOpacityOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-opacity-option';
import {ContextualMenuHeadingOption} from '@Components/contextual-menu/components/contextual-menu-heading-option';
import {ItemShadowOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-shadow-option';
import {ItemGlowOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-glow-option';
import {ItemBorderOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-border-option';
import {ItemBlackAndWhiteOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-black-and-white-option';
import {ItemSepiaOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-sepia-option';
import {ItemInvertOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-invert-option';
import {ItemBrightnessOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-brightness-option';
import {ItemContrastOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-contrast-option';
import {ItemVibranceOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-vibrance-option';
import {ItemSaturationOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-saturation-option';
import {ItemTintOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-tint-option';
import {ItemMultiplyOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-multiply-option';
import {ItemGammaOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-gamma-option';
import {ItemInteractivityLinkOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-interactivity-link-option';
import {InformationLinkOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/information-link-option';
import {VideoItemPanels} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-video-options/poster-editing-video-options.types';
import {CustomShadowColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/custom-shadow-color-panel';
import {ItemBorderColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-border-color-panel';
import {ItemGlowColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-glow-color-panel';
import {ItemMultiplyColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-multiply-color-panel';
import {ItemTintColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-tint-color-panel';
import {VideoMediaControls} from '@Components/poster-editor/components/poster-editing-side-panel/components/video-media-controls';
import {ItemLockInformationBox} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-lock-information-box';
import {ItemEdgeEffectsOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-edge-effects-option';
import {ContextualMenuTextHeader} from '@Components/contextual-menu/components/contextual-menu-text-header';
import {ContextualMenuHeader} from '@Components/contextual-menu/components/contextual-menu-header';
import AiSubtitlePanel from '@Panels/ai-subtitle-panel/ai-subtitle-panel';
import styles from './poster-editing-video-options.module.scss';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useIsOpenedVideoPanelValid} from '@Hooks/poster-editor/useIsOpenedVideoPanelValid';
import {VideoItemGenerateAiSubtitlesOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/generate-ai-subtitles-option/components/video-item-generate-ai-subtitles-option/video-item-generate-ai-subtitles-option';
import {ItemAnimationOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-animation-option/item-animation-option';
import {ItemAnimationPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-animation-panel/item-animation-panel';
import {ContextualMenuContentKeys} from '@Components/poster-editor/components/poster-editing-side-panel/components/contextual-options/contextual-options.types';
import {VideoItemVolumeOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/video-item-volume-option/video-item-volume-option';
import {VideoItemSpeedOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/video-item-speed-option/video-item-speed-option';

export function PosterEditingVideoOptions(): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const [currentItemHeaderTab, setCurrentItemHeaderTab] = useState(ItemHeaderTab.EDIT);
  const [currentlyOpenedPanel, setCurrentlyOpenedPanel] = useState(VideoItemPanels.NONE);

  useIsOpenedVideoPanelValid(currentlyOpenedPanel, setCurrentlyOpenedPanel);

  if (!activeItem) {
    throw new Error('Active item does not exist in video options');
  }

  if (!activeItem.isVideo()) {
    throw new Error(`Expected item to be of type Video - instead got ${activeItem.gitype}`);
  }

  const isSubPanelOpened = (): boolean => {
    return currentlyOpenedPanel !== VideoItemPanels.NONE;
  };

  const getHeaderText = (): string => {
    if (currentItemHeaderTab === ItemHeaderTab.POSITION) {
      return window.i18next.t('pmwjs_position');
    }
    if (currentlyOpenedPanel === VideoItemPanels.ANIMATION) {
      return window.i18next.t('pmwjs_animation');
    } else if (currentlyOpenedPanel === VideoItemPanels.AI_SUBTITLES) {
      return window.i18next.t('pmwjs_ai_subtitles');
    } else {
      return window.i18next.t('pmwjs_color');
    }
  };

  const getHeader = (): ReactElement<any> | null => {
    if (isSubPanelOpened() || currentItemHeaderTab === ItemHeaderTab.POSITION) {
      return (
        <ContextualMenuHeader id="video-panel-header">
          <ContextualMenuTextHeader
            showBackButton
            onBackButton={(): void => {
              if (currentItemHeaderTab === ItemHeaderTab.POSITION) {
                setCurrentItemHeaderTab(ItemHeaderTab.EDIT);
              }
              setCurrentlyOpenedPanel(VideoItemPanels.NONE);
            }}
            text={getHeaderText()}
          />
        </ContextualMenuHeader>
      );
    }
    return null;
  };

  const getPositionOptions = (): ReactElement<any> => {
    return <ItemPositionContextualOptions />;
  };

  const getEditOptions = (): ReactElement<any> => {
    return (
      <>
        <ItemLockInformationBox />
        <PosterItemControls
          onClickPosition={(): void => {
            setCurrentItemHeaderTab(ItemHeaderTab.POSITION);
          }}
        />
        <VideoMediaControls />
        <ItemOpacityOption />
        <ItemAnimationOption
          onAnimationTypeClick={(): void => {
            setCurrentlyOpenedPanel(VideoItemPanels.ANIMATION);
          }}
        />
        <VideoItemGenerateAiSubtitlesOption />
        <VideoItemVolumeOption />
        <VideoItemSpeedOption />
        <ContextualMenuHeadingOption id="image-effects-heading-option" text={window.i18next.t('pmwjs_effects')} />
        <ItemEdgeEffectsOption />
        <ItemShadowOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(VideoItemPanels.SHADOW);
          }}
        />
        <ItemGlowOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(VideoItemPanels.GLOW);
          }}
        />
        <ItemBorderOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(VideoItemPanels.BORDER);
          }}
        />
        <ItemBlackAndWhiteOption />
        <ItemSepiaOption />
        <ItemInvertOption />
        <ContextualMenuHeadingOption id="image-adjustments-heading-option" text={window.i18next.t('pmwjs_adjustments')} />
        <ItemBrightnessOption />
        <ItemContrastOption />
        <ItemVibranceOption />
        <ItemSaturationOption />
        <ItemTintOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(VideoItemPanels.TINT);
          }}
        />
        <ItemMultiplyOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(VideoItemPanels.MULTIPLY);
          }}
        />
        <ItemGammaOption />
        <ContextualMenuHeadingOption id="sticker-fill-interactivity-option" text={window.i18next.t('pmwjs_interactivity')} />
        <ItemInteractivityLinkOption />
        <InformationLinkOption />
      </>
    );
  };

  const getAiSubtitlesSubPanel = (): ReactElement<any> => {
    return <AiSubtitlePanel contentClasses={styles.content} hideHeader />;
  };

  const getSubPanel = (): ReactElement<any> => {
    switch (currentlyOpenedPanel) {
      case VideoItemPanels.SHADOW:
        return <CustomShadowColorPanel />;
      case VideoItemPanels.BORDER:
        return <ItemBorderColorPanel />;
      case VideoItemPanels.GLOW:
        return <ItemGlowColorPanel />;
      case VideoItemPanels.MULTIPLY:
        return <ItemMultiplyColorPanel />;
      case VideoItemPanels.TINT:
        return <ItemTintColorPanel />;
      case VideoItemPanels.AI_SUBTITLES:
        return getAiSubtitlesSubPanel();
      case VideoItemPanels.ANIMATION:
        return <ItemAnimationPanel />;
      default:
        throw new Error(`unhandled sub panel case ${currentlyOpenedPanel} for image item`);
    }
  };

  const getOptions = (): ReactElement<any> => {
    if (isSubPanelOpened()) {
      return getSubPanel();
    }

    switch (currentItemHeaderTab) {
      case ItemHeaderTab.POSITION:
        return getPositionOptions();
      case ItemHeaderTab.EDIT:
        return getEditOptions();
      default:
        throw new Error('unhandled tab item for contextual header');
    }
  };

  const getKey = (): string => {
    if (!isSubPanelOpened()) {
      return currentItemHeaderTab === ItemHeaderTab.POSITION ? ContextualMenuContentKeys.VIDEO_DEFAULT_POSITION : ContextualMenuContentKeys.VIDEO_DEFAULT_EDIT;
    }

    return '';
  };

  return useMemo((): ReactElement<any> => {
    return (
      <ContextualMenu contentKey={getKey()} id="video-contextual-options" includeLeftRightPadding={!isSubPanelOpened()} header={getHeader()}>
        {getOptions()}
      </ContextualMenu>
    );
  }, [currentlyOpenedPanel, currentItemHeaderTab]);
}
